import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  HStack,
  Wrap,
  WrapItem,
  Tag,
  Button,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Δείγμα δεδομένων — αντικατάστησέ το με τα δικά σου ή πέρασε items ως prop
const eventsDataEL = [
  {
    id: 1,
    title: "Τουρνουά Bowling",
    date: "2025-10-08T17:30:00",
    location: "Arena Bowling Center",
    tags: ["Bowling", "Τουρνουά"],
    description: "Εσωτερικό τουρνουά για όλα τα επίπεδα. Ατομικό!",
    details: "Κανόνες: Οι συμμετέχοντες πρέπει να φέρουν τον δικό τους εξοπλισμό. Παροχές: Μπάλες/παπούτσια διαθέσιμα. Δηλώσεις μέχρι 23/9. Κόστος συμμετοχής: 12€.",
  },
  {
    id: 2,
    title: "Kids Day & Arcade",
    date: "2025-09-10T11:00:00",
    location: "Arena Arcade",
    tags: ["Παιδιά", "Οικογένεια"],
    description: "Ημέρα διασκέδασης για παιδιά με παιχνίδια arcade και bowling.",
    details: "Κανόνες: Τα παιδιά πρέπει να συνοδεύονται από ενήλικα. Φαγητό & πορτοκαλάδες για παιδιά κάθε Κυριακή 11:00-14:00. Κρατήσεις απαραίτητες.",
  },
  {
    id: 3,
    title: "Red Pin Challenge",
    date: "2025-11-30T18:00:00",
    location: "Arena Billiards",
    tags: ["Bowling", "Πρόκληση"],
    description: "Δοκίμασε τις ικανότητές σου στο Bowling και ρίξε την κόκκινη κορίνα. Ο νικητής κερδίζει 1 δωρεάν παιχνίδι bowling!",
    details: "Κανόνες: Φόρμα: Double elimination, best of 3. Εγγραφή στον χώρο έως 17:45. Έπαθλα για τους 3 πρώτους και bonus δώρο για το καλύτερο break.",
  },
  {
    id: 4,
    title: "Πρωτοχρονιάτικο Bowling Bash",
    date: "2024-12-30T20:00:00",
    location: "Arena Bowling Center",
    tags: ["Party"],
    description: "Πρωτοχρονιάτικο party με μουσική και δώρα.",
    details: "Κανόνες: Η είσοδος επιτρέπεται μόνο με πρόσκληση. Happy hour όλο το βράδυ, DJ set, δώρα έκπληξη.",
  },
];

const eventsDataEN = [
  {
    id: 1,
    title: "Bowling Tournament",
    date: "2025-10-08T17:30:00",
    location: "Arena Bowling Center",
    tags: ["Bowling", "Tournament"],
    description: "Internal tournament for all levels. Individual!",
    details: "Rules: Participants must bring their own equipment. Provided: Balls/shoes available. Registration until 23/9. Entry fee: €12.",
  },
  {
    id: 2,
    title: "Kids Day & Arcade",
    date: "2025-09-10T11:00:00",
    location: "Arena Arcade",
    tags: ["Kids", "Family"],
    description: "Fun day for kids with arcade games and bowling.",
    details: "Rules: Children must be accompanied by an adult. Food & orange juice for kids every Sunday 11:00-14:00. Reservations required.",
  },
  {
    id: 3,
    title: "Red Pin Challenge",
    date: "2025-11-30T18:00:00",
    location: "Arena Billiards",
    tags: ["Bowling", "Challenge"],
    description: "Test your bowling skills and knock down the red pin. The winner gets 1 free bowling game!",
    details: "Rules: Format: Double elimination, best of 3. Registration on site until 17:45. Prizes for the top 3 and a bonus gift for the best break.",
  },
  {
    id: 4,
    title: "New Year's Bowling Bash",
    date: "2024-12-30T20:00:00",
    location: "Arena Bowling Center",
    tags: ["Party"],
    description: "New Year's party with music and gifts.",
    details: "Rules: Entry is by invitation only. Happy hour all night, DJ set, surprise gifts.",
  },
];

function formatDate(str) {
  return new Date(str).toLocaleDateString("el-GR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Events() {
  const { t, i18n } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState(null);

  const sixMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d;
  }, []);

  // Επιλογή σωστού array ανάλογα με τη γλώσσα
  const eventsData = i18n.language === "en" ? eventsDataEN : eventsDataEL;

  // Πηγή δεδομένων
  const source = useMemo(() => [...eventsData], [eventsData]);

  // Ταξινόμηση κατά ημερομηνία (νεότερα πρώτα)
  const sortedEvents = useMemo(
    () => [...source].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [source]
  );

  // Διαχωρισμός: πρόσφατα (>= sixMonthsAgo) και παλαιότερα
  const recentEvents = useMemo(
    () => sortedEvents.filter((e) => new Date(e.date) >= sixMonthsAgo),
    [sortedEvents, sixMonthsAgo]
  );
  const olderEvents = useMemo(
    () => sortedEvents.filter((e) => new Date(e.date) < sixMonthsAgo),
    [sortedEvents, sixMonthsAgo]
  );

  // Εμφάνιση/κρύψιμο παλαιότερων
  const [showOlder, setShowOlder] = useState(false);

  const openDetails = (eventObj) => {
    setSelected(eventObj);
    onOpen();
  };

  // Single card component (όλη η κάρτα clickable)
  const Card = (e) => (
    <Box
      key={e.id}
      p={5}
      borderRadius="2xl"
      bg="whiteAlpha.100"
      _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
      transition="all .2s ease"
      boxShadow="0 10px 30px rgba(0,0,0,.35)"
      onClick={() => openDetails(e)}
      cursor="pointer"
      role="button"
      tabIndex={0}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          openDetails(e);
        }
      }}
      _focus={{ outline: "none", boxShadow: "0 0 0 2px rgba(118, 118, 118, 0.3)" }}
    >
      <HStack justify="space-between" mb={2} spacing={2} align="start">
        <Badge colorScheme="blackAlpha" variant="outline">
          {formatDate(e.date)}
        </Badge>
        <Text fontSize="sm" opacity={0.8} noOfLines={1}>
          {e.location}
        </Text>
      </HStack>

      <Heading size="md" mb={2}>
        {e.title}
      </Heading>

      <Text opacity={0.9} mb={4} noOfLines={3}>
        {e.description}
      </Text>

      <Wrap spacing={2} mb={4}>
        {e.tags?.map((t) => (
          <WrapItem key={t}>
            <Tag variant="subtle" colorScheme="blackAlpha">
              {t}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>

      <Button
        size="sm"
        variant="outline"
        _hover={{ bg: "whiteAlpha.200" }}
        onClick={(ev) => {
          ev.stopPropagation(); 
          openDetails(e);
        }}
      >
        Μάθε περισσότερα
      </Button>
    </Box>
  );

  return (
    <Box bg="white" color="black" py={{ base: 12, md: 16 }}>
      <Container maxW="container.xl">
        <Stack spacing={3} mb={{ base: 6, md: 10 }}>
          <Heading size={{ base: "xl", md: "2xl" }}>{t("events_title")}</Heading>
          <Text opacity={0.9}>{t("events_description")}</Text>
        </Stack>

        {/* Πρόσφατα (τελευταίο 6μήνο) */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, md: 8 }}>
          {recentEvents.length ? (
            recentEvents.map((e) => Card(e))
          ) : (
            <Box opacity={0.8}>{t("events_no_recent")}</Box>
          )}
        </SimpleGrid>

        {/* Κουμπί "Περισσότερα..." για παλαιότερα */}
        {olderEvents.length > 0 && !showOlder && (
          <Stack align="center" pt={{ base: 6, md: 8 }}>
            <Button
              variant="outline"
              _hover={{ bg: "blackAlpha.200" }}
              onClick={() => setShowOlder(true)}
            >
              {t("events_more")}
            </Button>
          </Stack>
        )}

        {/* Παλαιότερα (εμφανίζονται όταν πατηθεί το Περισσότερα...) */}
        {showOlder && olderEvents.length > 0 && (
          <Stack spacing={4} pt={{ base: 8, md: 10 }}>
            <Divider borderColor="blackAlpha.300" />
            <Heading size="md" opacity={0.9}>{t("events_older_title")}</Heading>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 6, md: 8 }}>
              {olderEvents.map((e) => Card(e))}
            </SimpleGrid>

            <Stack align="center">
              <Button
                variant="ghost"
                colorScheme="blackAlpha"
                onClick={() => setShowOlder(false)}
              >
                {t("events_less")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Container>

      {/* Modal λεπτομερειών — responsive (90% σε mobile) */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setSelected(null);
          onClose();
        }}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent
          bg="white"
          color="black"
          w={{ base: "90vw", sm: "90vw", md: "640px" }}
          maxW={{ base: "90vw", md: "640px" }}
          m={{ base: 4, md: 6 }}
          borderRadius="2xl"
        >
          <ModalHeader>{selected?.title ?? t("events_details")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH={{ base: "60vh", md: "70vh" }} overflowY="auto">
            {selected && (
              <Stack spacing={3}>
                <Stack
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  spacing={2}
                >
                  <Badge background={"transparent"} padding={"0 0 0 0"} fontSize={"lg"}>
                    {formatDate(selected.date)}
                  </Badge>
                  <Text fontSize="md" opacity={0.8}>
                    {selected.location}
                  </Text>
                </Stack>

                <Divider borderColor="blackAlpha.300" />

                {/* Description */}
                <Text fontSize="md" opacity={0.95} fontWeight="bold">
                  {selected.description}
                </Text>

                <Stack spacing={2} fontSize="sm" color="gray.700">
                  {selected.details.split(". ").map((line, idx) => (
                    <Text key={idx}>• {line}</Text>
                  ))}
                </Stack>

                {selected.tags?.length ? (
                  <Wrap spacing={2} pt={2}>
                    {selected.tags.map((t) => (
                      <WrapItem key={t}>
                        <Tag variant="subtle" colorScheme="blackAlpha">
                          {t}
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                ) : null}
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                setSelected(null);
                onClose();
              }}
              variant="outline"
              _hover={{ bg: "blackAlpha.200" }}
            >
              {t("events_close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
